import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ShoppingBag, Wrench } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { SITE } from "../../site.config";
import { SERVICES } from "../../data/services";
import QuoteButton from "./QuoteButton";
import PromoBar from "./PromoBar";

const NAV = [
  { label: "Home", href: "#home", type: "anchor" },
  { label: "About", href: "/about", type: "page" },
  { label: "Service Area", href: "#areas", type: "anchor" },
  { label: "Pricing", href: "#pricing", type: "anchor" },
  { label: "Reviews", href: "/reviews", type: "page" },
  { label: "Builds", href: "/builds", type: "page" },
  { label: "Advice", href: "/blog", type: "page" },
  { label: "FAQ", href: "#faq", type: "anchor" },
  { label: "Contact", href: "#contact", type: "anchor" },
];

const SCROLL_THRESHOLD_PX = 30;

// Render the correct nav element for a single NAV entry.
// Extracted to avoid a nested ternary inside the JSX (improves readability).
function NavLink({ item, anchorHref }) {
  const testId = `nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;

  if (item.type === "page") {
    return (
      <Link
        to={item.href}
        data-testid={testId}
        className="bebas text-base tracking-[0.18em] text-white/80 hover:text-cyan-pz transition-colors whitespace-nowrap"
      >
        {item.label}
      </Link>
    );
  }

  if (item.label === "Pricing") {
    return (
      <a
        href={anchorHref(item.href)}
        data-testid={testId}
        className="bebas text-base tracking-[0.18em] text-cyan-pz border border-cyan-pz/50 px-3 py-1.5 hover:bg-cyan-pz hover:text-black transition-colors clip-tag whitespace-nowrap"
      >
        {item.label}
      </a>
    );
  }

  return (
    <a
      href={anchorHref(item.href)}
      data-testid={testId}
      className="bebas text-base tracking-[0.18em] text-white/80 hover:text-cyan-pz transition-colors whitespace-nowrap"
    >
      {item.label}
    </a>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // For anchor links from non-home pages, fall back to "/<href>"
  const anchorHref = (href) =>
    typeof window !== "undefined" && window.location.pathname !== "/"
      ? `/${href}`
      : href;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/90 backdrop-blur-md border-b border-white/10"
          : "bg-gradient-to-b from-black/80 to-transparent"
      }`}
    >
      <PromoBar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-32 md:h-40 lg:h-48 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={SITE.business.logo}
            alt={SITE.business.fullName}
            className="h-24 md:h-32 lg:h-40 w-auto drop-glow-cyan transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV.map((item) => (
            <React.Fragment key={item.href}>
              <NavLink item={item} anchorHref={anchorHref} />

              {/* Insert Services dropdown immediately after Home */}
              {item.label === "Home" && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="nav-services"
                    className="bebas text-base tracking-[0.18em] text-white/80 hover:text-cyan-pz transition-colors inline-flex items-center gap-1 outline-none focus:outline-none"
                  >
                    Services
                    <ChevronDown className="w-3.5 h-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    data-testid="nav-services-menu"
                    className="bg-black border border-cyan-pz/40 text-white min-w-[280px] rounded-none clip-perf p-0"
                  >
                    {SERVICES.map((s) => (
                      <DropdownMenuItem key={s.slug} asChild className="rounded-none cursor-pointer text-white focus:bg-[#5EE6E6]/10 focus:text-[#5EE6E6] data-[highlighted]:bg-[#5EE6E6]/10 data-[highlighted]:text-[#5EE6E6] px-0 py-0">
                        <Link
                          to={`/services/${s.slug}`}
                          data-testid={`nav-services-${s.slug}`}
                          className="w-full px-4 py-3 flex items-start gap-3 border-b border-white/5 last:border-b-0"
                        >
                          <Wrench className="w-4 h-4 text-cyan-pz mt-1 shrink-0" />
                          <div>
                            <p className="bebas tracking-widest text-base">
                              {s.name.toUpperCase()}
                            </p>
                            <p className="text-white/50 text-xs barlow">
                              {s.kicker}
                            </p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </React.Fragment>
          ))}

          {/* Shop dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-shop"
              className="bebas text-base tracking-[0.18em] text-white/80 hover:text-cyan-pz transition-colors inline-flex items-center gap-1 outline-none focus:outline-none relative"
            >
              <span
                data-testid="shop-coming-soon"
                className="absolute -top-3 -right-3 bg-cyan-pz text-black bebas text-[9px] tracking-widest px-1.5 py-0.5 leading-none rotate-6 shadow-[0_0_10px_rgba(94,230,230,0.4)]"
              >
                COMING SOON
              </span>
              Shop
              <ChevronDown className="w-3.5 h-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="nav-shop-menu"
              className="bg-black border border-cyan-pz/40 text-white min-w-[240px] rounded-none clip-perf p-0"
            >
              <DropdownMenuItem asChild className="rounded-none cursor-pointer text-white focus:bg-[#5EE6E6]/10 focus:text-[#5EE6E6] data-[highlighted]:bg-[#5EE6E6]/10 data-[highlighted]:text-[#5EE6E6] px-0 py-0">
                <Link
                  to="/shop"
                  data-testid="nav-shop-all"
                  className="w-full bebas tracking-widest px-4 py-3 border-b border-white/10"
                >
                  ALL PRODUCTS
                </Link>
              </DropdownMenuItem>
              {SITE.shop.categories.map((c) => (
                <DropdownMenuItem key={c.slug} asChild className="rounded-none cursor-pointer text-white focus:bg-[#5EE6E6]/10 focus:text-[#5EE6E6] data-[highlighted]:bg-[#5EE6E6]/10 data-[highlighted]:text-[#5EE6E6] px-0 py-0">
                  <Link
                    to={`/shop?category=${c.slug}`}
                    data-testid={`nav-shop-${c.slug}`}
                    className="w-full px-4 py-3 flex items-start gap-3"
                  >
                    <ShoppingBag className="w-4 h-4 text-cyan-pz mt-1 shrink-0" />
                    <div>
                      <p className="bebas tracking-widest text-base">
                        {c.name.toUpperCase()}
                      </p>
                      <p className="text-white/50 text-xs barlow">
                        {c.kicker}
                      </p>
                    </div>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-3">
          <QuoteButton variant="header" testId="header-quote-btn" />
          <a
            href={`tel:${SITE.business.phoneTel}`}
            data-testid="header-call-btn"
            className="hidden md:inline-flex whitespace-nowrap items-center gap-2 bg-cyan-pz text-black px-5 py-3 bebas text-lg tracking-widest hover:bg-white transition-colors clip-tag"
          >
            <Phone className="w-4 h-4" />
            {SITE.business.phone}
          </a>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-black max-h-[calc(100vh-6rem)] overflow-y-auto"
        >
          <div className="flex flex-col px-5 py-6 gap-4">
            {NAV.map((item) =>
              item.type === "page" ? (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className="bebas text-2xl tracking-widest text-white hover:text-cyan-pz"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={anchorHref(item.href)}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className="bebas text-2xl tracking-widest text-white hover:text-cyan-pz"
                >
                  {item.label}
                </a>
              )
            )}

            {/* Mobile Services */}
            <div className="mt-2 pt-4 border-t border-white/10">
              <p className="bebas text-cyan-pz tracking-widest text-sm mb-3">
                SERVICES
              </p>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-services-${s.slug}`}
                  className="block bebas text-xl tracking-widest text-white/80 hover:text-cyan-pz mb-2 pl-3 border-l border-cyan-pz/30"
                >
                  {s.name}
                </Link>
              ))}
            </div>

            {/* Mobile Shop */}
            <div className="mt-2 pt-4 border-t border-white/10">
              <p className="bebas text-cyan-pz tracking-widest text-sm mb-3 inline-flex items-center gap-2">
                SHOP
                <span className="bg-cyan-pz text-black bebas text-[9px] tracking-widest px-1.5 py-0.5 leading-none">
                  COMING SOON
                </span>
              </p>
              <Link
                to="/shop"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-shop-all"
                className="block bebas text-xl tracking-widest text-white hover:text-cyan-pz mb-2"
              >
                All Products
              </Link>
              {SITE.shop.categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/shop?category=${c.slug}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-shop-${c.slug}`}
                  className="block bebas text-xl tracking-widest text-white/80 hover:text-cyan-pz mb-2 pl-3 border-l border-cyan-pz/30"
                >
                  {c.name}
                </Link>
              ))}
            </div>

            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="mobile-call-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-cyan-pz text-black px-5 py-4 bebas text-xl tracking-widest"
            >
              <Phone className="w-5 h-5" />
              CALL {SITE.business.phone}
            </a>
            <QuoteButton variant="mobile" testId="mobile-quote-btn" />
          </div>
        </div>
      )}
    </header>
  );
}
